import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, MapPin, Hash, FolderOpen, ChevronRight,
  Loader2, BarChart3, FileText, Briefcase, ArrowRight, Search,
} from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

function CompanyTile({ company, onEnter, onOpenFiles, onOpenReports }) {
  const d = company.details || {};

  const handleClick = () => {
    onEnter(company, '/dashboard');
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all group flex flex-col"
      data-testid={`company-tile-${company.slug}`}
    >
      {/* Accent bar */}
      <div className="h-1.5" style={{ backgroundColor: company.color || '#6366F1' }} />

      <div className="p-5 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-4">
          <button
            onClick={handleClick}
            className="flex items-start gap-3 text-left flex-1 min-w-0"
            data-testid={`company-tile-enter-${company.slug}`}
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold"
              style={{ backgroundColor: company.color || '#6366F1' }}
            >
              {(company.name?.[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                {company.name}
              </h3>
              {company.description && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{company.description}</p>
              )}
            </div>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-70 group-hover:opacity-100"
                data-testid={`company-tile-menu-${company.slug}`}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={handleClick}>
                <Briefcase className="w-4 h-4 mr-2" />
                Open dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onOpenFiles(company)}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Open main folder
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onOpenReports(company)}>
                <BarChart3 className="w-4 h-4 mr-2" />
                View reports
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEnter(company, '/clients')}>
                <FileText className="w-4 h-4 mr-2" />
                {company.slug === 'cabex' ? 'Cabex clients' : 'Contact list'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Details */}
        <dl className="space-y-2 text-sm flex-1">
          <DetailRow
            icon={Building2}
            label="Legal Name"
            value={d.legal_name || company.name}
          />
          {d.business_address && (
            <DetailRow icon={MapPin} label="Address" value={d.business_address} clamp />
          )}
          {d.registered_agent && (
            <DetailRow icon={Briefcase} label="Registered Agent" value={d.registered_agent} />
          )}
          {d.formation_state && (
            <DetailRow icon={MapPin} label="State" value={d.formation_state} />
          )}
          {d.ein && <DetailRow icon={Hash} label="Tax ID" value={d.ein} />}
          {!d.business_address && !d.registered_agent && !d.formation_state && !d.ein && (
            <div className="text-xs text-gray-400 italic mt-1">
              No company details yet — open this company and click "Edit" to add EIN, address, registered agent, etc.
            </div>
          )}
        </dl>

        {/* Footer actions */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-start text-gray-600 hover:text-gray-900"
            onClick={() => onOpenFiles(company)}
            data-testid={`company-tile-folder-${company.slug}`}
          >
            <FolderOpen className="w-4 h-4 mr-2 text-blue-500" />
            Main folder
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700"
            onClick={handleClick}
          >
            Open <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, clamp }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="text-xs text-gray-400 mr-1.5 uppercase tracking-wide">{label}</span>
        <span className={`text-sm text-gray-700 ${clamp ? 'line-clamp-2' : 'truncate block sm:inline'}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { companies, switchCompany, loading } = useCompany();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Workspaces • BusinessHub';
  }, []);

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => {
      const d = c.details || {};
      return [
        c.name,
        c.slug,
        c.description,
        d.legal_name,
        d.business_address,
        d.registered_agent,
        d.registered_agent_address,
        d.formation_state,
        d.ein,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [companies, search]);

  const handleEnter = (company, path = '/dashboard') => {
    switchCompany(company);
    navigate(path);
  };

  const handleOpenFiles = (company) => {
    switchCompany(company);
    navigate(company.is_personal ? '/personal/files' : '/files');
  };

  const handleOpenReports = (company) => {
    switchCompany(company);
    if (company.slug === 'cabex') {
      navigate('/cabex/reports');
    } else {
      navigate('/reports');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const businessCompanies = filteredCompanies.filter((c) => !c.is_personal);
  const personalCompanies = filteredCompanies.filter((c) => c.is_personal);
  const totalBusinesses = companies.filter((c) => !c.is_personal).length;
  const totalPersonal = companies.filter((c) => c.is_personal).length;
  const hasResults = businessCompanies.length + personalCompanies.length > 0;

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Your Workspaces</h1>
          <p className="text-gray-500 mt-1">
            Click any company tile to open it, or use the menu to jump straight to files or reports.
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {totalBusinesses} business{totalBusinesses === 1 ? '' : 'es'} · {totalPersonal} personal
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search companies by name, state, tax ID, registered agent..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-9 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="workspaces-search"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xs px-2 py-1"
            data-testid="workspaces-search-clear"
          >
            Clear
          </button>
        )}
      </div>

      {!hasResults ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No companies match "{search}"</h3>
          <p className="text-sm text-gray-500 mb-4">Try a different name, state, or tax ID.</p>
          <button
            onClick={() => setSearch('')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Clear search
          </button>
        </div>
      ) : (
        <>
          {/* Business companies grid */}
          {businessCompanies.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Business {search && `(${businessCompanies.length})`}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {businessCompanies.map((c) => (
                  <CompanyTile
                    key={c.id}
                    company={c}
                    onEnter={handleEnter}
                    onOpenFiles={handleOpenFiles}
                    onOpenReports={handleOpenReports}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Personal */}
          {personalCompanies.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Personal {search && `(${personalCompanies.length})`}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {personalCompanies.map((c) => (
                  <CompanyTile
                    key={c.id}
                    company={c}
                    onEnter={handleEnter}
                    onOpenFiles={handleOpenFiles}
                    onOpenReports={handleOpenReports}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
